<script lang="ts">
    import { onMount } from 'svelte/internal'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import FormV2PrintDocument from '../customer-service-v2/forms/form-v2-print-document.svelte'
    import { createDefaultHeaderFooter } from '../customer-service-v2/types/document-header-footer'
    import { readContextSnapshot, type FillContext } from '../customer-service-v2/forms/context-snapshot'
    import type { FormV2Form } from '../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'

    // Página pública dedicada só à renderização em PDF (consumida pela lambda-pdf via Puppeteer) —
    // autenticada pelo mesmo mecanismo de token do link público de preenchimento, mas com um link de
    // vida curta gerado especificamente para essa renderização (ver start-signing no backend). Como
    // esse link nunca é digitado/copiado por uma pessoa, usa linkId/token crus na query string, sem a
    // compactação em base58 usada no link de preenchimento (que existe só para encurtar o link para humanos).
    let show = true
    let error = ''
    let form: FormV2Form | null = null
    // Congelado dentro do documento no último salvamento pelo editor logado (ver context-snapshot.ts).
    // Substitui as tags {} do texto fixo ({nome}, {compra}, {vendedor}, {atendente}, {usuario}) neste
    // PDF. Lê só do snapshot, sem cair de volta em dado vivo: como é este HTML que a lambda-pdf
    // converte no PDF que segue para assinatura, um fallback reintroduziria — de forma intermitente —
    // a divergência entre o que foi assinado e o que se vê depois.
    let printContext: FillContext = {}
    // "?pdf=1" só é adicionado pelo backend ao gerar o link de impressão consumido pela lambda-pdf —
    // distingue essa renderização (que deve exibir o ID do documento no rodapé) de uma eventual
    // visita direta a essa página por uma pessoa.
    let isPdfRender = false

    onMount(load)

    async function load() {
        try {
            show = true
            const urlParams = new URLSearchParams(window.location.search)
            const linkId = urlParams.get('linkId')
            // "t", não "token": "token" já é usado em toda a SPA para o JWT da sessão logada, e um
            // usuário autenticado abrindo este link público colidia com esse outro parâmetro.
            const token = urlParams.get('t')
            isPdfRender = urlParams.get('pdf') === '1'
            if (!linkId || !token) {
                error = 'Link inválido.'
                return
            }
            const result = await window.api.tools.formsV2.forms.publicGetOne(linkId, token)
            form = result.form
            printContext = readContextSnapshot((form.document as any)?.contextSnapshot)
        } catch (err) {
            error = 'Este link não está mais disponível.'
        } finally {
            show = false
        }
    }
</script>

<div class="root">
    <LoadSpinner {show} />
    {#if error}
        <div class="msg">{error}</div>
    {:else if form}
        <!-- Mesmo componente usado na impressão dentro do app (ver form-v2-print.helper.ts): o timbre
             se repete em toda página e o PDF gerado pela lambda sai visualmente igual ao documento
             impresso pela equipe — importante porque é este PDF que segue para assinatura. -->
        <FormV2PrintDocument
            blocks={form.document?.templateSnapshot?.blocks ?? []}
            headerFooter={form.document?.templateSnapshot?.headerFooter ?? createDefaultHeaderFooter()}
            answers={form.document?.answers ?? {}}
            companyId={form.companyId}
            documentFooterId={isPdfRender ? form.formV2FormId : null}
            context={printContext}
        />
    {/if}
</div>

<style>
    .root {
        min-height: 100vh;
        background: #fff;
        padding: 16px;
    }
    .msg {
        max-width: 1000px;
        margin: 40px auto;
        text-align: center;
        color: #495057;
    }
</style>
